import { CcPlatformSdk } from '@cc-consulting-nv/ccsdk';
import type { AuthTokens } from '@cc-consulting-nv/ccsdk';

// SDK singleton instance
let sdkInstance: CcPlatformSdk | null = null;

// Storage keys (matching Vue app pattern)
const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'auth_user';

/**
 * Resolve the API base URL.
 * - Dev: VITE_API_HOST env var (e.g. http://localhost:8000)
 * - Prod: window.location.origin (single build works on any domain)
 */
export function resolveApiBaseUrl(): string {
  const envHost = import.meta.env.VITE_API_HOST;
  if (envHost) {
    return String(envHost).replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  return 'http://localhost:8000';
}

/**
 * Read initial tokens from localStorage (synchronous, avoids circular deps).
 */
function getInitialTokens(): AuthTokens | undefined {
  const storedToken = localStorage.getItem(TOKEN_KEY);
  const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);

  if (storedToken) {
    return {
      accessToken: storedToken,
      refreshToken: storedRefresh || undefined,
    };
  }

  const envToken = import.meta.env.VITE_API_TOKEN;
  if (envToken) {
    return { accessToken: String(envToken) };
  }

  return undefined;
}

/**
 * Attempt to refresh the access token using the refresh token.
 * Uses raw fetch to avoid infinite loop through the SDK.
 */
async function refreshTokens(): Promise<AuthTokens> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const baseUrl = resolveApiBaseUrl();
  const response = await fetch(`${baseUrl}/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) {
    throw new Error(`Token refresh failed: ${response.status}`);
  }

  const data = await response.json();
  const newAccessToken = data.access_token || data.accessToken || data.data?.access_token;
  const newRefreshToken = data.refresh_token || data.refreshToken || data.data?.refresh_token;

  if (!newAccessToken) {
    throw new Error('No access token in refresh response');
  }

  const newTokens: AuthTokens = {
    accessToken: newAccessToken,
    refreshToken: newRefreshToken || refreshToken,
  };

  // Persist new tokens
  if (newTokens.accessToken) {
    localStorage.setItem(TOKEN_KEY, newTokens.accessToken);
  }
  if (newTokens.refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, newTokens.refreshToken);
  }

  // Update SDK
  sdkInstance?.setTokens(newTokens);

  return newTokens;
}

/**
 * Handle token expiration — clear storage and redirect to login.
 */
async function handleTokenExpiration(): Promise<void> {
  const hadToken = Boolean(localStorage.getItem(TOKEN_KEY));

  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  sdkInstance?.setTokens(null);

  try {
    await sdkInstance?.clearCache();
  } catch {
    // ignore cache clear errors
  }

  if (hadToken) {
    window.location.href = '/login';
  }
}

/**
 * Initialize the SDK singleton.
 */
function initializeSdk(): CcPlatformSdk {
  if (sdkInstance) return sdkInstance;

  const baseUrl = resolveApiBaseUrl();
  const tokens = getInitialTokens();

  const envLogging = import.meta.env.VITE_SDK_ENABLE_LOGGING;
  const enableLogging = envLogging === 'true' || envLogging === '1' || envLogging === true;

  sdkInstance = new CcPlatformSdk({
    baseUrl,
    tokens,
    enableLogging: Boolean(enableLogging),
    onRefreshTokens: () => refreshTokens(),
    onUnauthorized: () => handleTokenExpiration(),
  });

  return sdkInstance;
}

/**
 * Get the SDK singleton. Initializes lazily on first call.
 */
export function getSdk(): CcPlatformSdk {
  if (!sdkInstance) {
    initializeSdk();
  }
  return sdkInstance!;
}

/**
 * Token helpers for the auth layer.
 */
export function getStoredTokens(): AuthTokens | null {
  const accessToken = localStorage.getItem(TOKEN_KEY);
  if (!accessToken) return null;
  return {
    accessToken,
    refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY) || undefined,
  };
}

export function storeTokens(tokens: AuthTokens): void {
  if (tokens.accessToken) {
    localStorage.setItem(TOKEN_KEY, tokens.accessToken);
  }
  if (tokens.refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  }
  sdkInstance?.setTokens(tokens);
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  sdkInstance?.setTokens(null);
}

export function getStoredUser<T>(): T | null {
  const json = localStorage.getItem(USER_KEY);
  if (!json) return null;
  try {
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

export function storeUser<T>(user: T): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export { TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY };
